import { API_BASE_URL } from '@synergy/config';
import { FilterParams } from '@synergy/models';
import { getLocalizedString } from '@synergy/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash.debounce';

import { ArticlesApi } from '../../../../../api/articles-api';
import {
    accountSelector,
    currencySelector,
    localesSelector,
    userSelector,
} from '../../../../../store/configuration-selectors';
import { isSupplier } from '../../../../../utils/is-supplier';
import { useAppSelector } from '../../../../../../store/store';

export interface SearchResultItem {
    id: number;
    name: string;
    count?: number;
}

export interface SearchResult {
    articles: SearchResultItem[];
    totalArticles: number;
    categories: SearchResultItem[];
    suppliers: SearchResultItem[];
    searchEventId?: number | null;
}

/**
 * The most article rows the dropdown can show: three categories, nine suppliers and
 * one more (see SearchAutocompleteContent). Every article asked for beyond that is
 * loaded with prices and images on each keystroke and never displayed.
 */
export const SUGGESTED_ARTICLES_LIMIT = 13;

export const useAutocompleteSearch = (value?: string): SearchResult | null => {
    const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
    const api = useRef(new ArticlesApi(API_BASE_URL)).current;
    const user = useAppSelector(userSelector);
    const account = useAppSelector(accountSelector);
    const locales = useAppSelector(localesSelector);
    const currency = useAppSelector(currencySelector);
    const latestRequest = useRef(0);

    const searchFunction = async (value?: string): Promise<void> => {
        const request = ++latestRequest.current;

        if (!value || value.length < 3) {
            setSearchResult(null);
            return;
        }

        const searchParams = new URLSearchParams({ [FilterParams.Search]: value });
        if (user && isSupplier(user) && account?.id) {
            searchParams.set(FilterParams.SupplierIds, account.id.toString());
        }
        const response = await api.getArticlesSearch(searchParams, undefined, 'autocomplete', SUGGESTED_ARTICLES_LIMIT);
        // A slower answer to an earlier keystroke must not replace the suggestions
        // for what is in the field now.
        if (request !== latestRequest.current) {
            return;
        }
        if (!response.success) {
            setSearchResult(null);
            return;
        }

        const result: SearchResult = {
            articles: response.result.items
                .map(article => ({
                    id: article.id,
                    name: getLocalizedString(locales, article.name),
                }))
                .filter(item => !!item.name),
            totalArticles: response.result.total,
            categories: (response.result.meta?.categories ?? [])
                .sort((a, b) => b.count - a.count)
                .filter(item => !!item.name),
            suppliers: (response.result.meta?.suppliers ?? []).sort((a, b) => b.count - a.count),
            searchEventId: response.result.meta?.searchEventId,
        };
        setSearchResult(result);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedApiSearch = useMemo(() => debounce(searchFunction, 400), [currency, locales]);

    useEffect(() => {
        debouncedApiSearch(value);
    }, [debouncedApiSearch, value]);

    return searchResult;
};
